import React, { useState, useEffect, useRef } from "react";
import "./Number.css";
import { fromMmToUnit, fromUnitToMm } from "src/helpers/dimensions";
import { useDebounce } from "use-debounce";

interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  realMin?: number;
  step: number;
  label?: string;
  showSubLabel?: boolean;
  subStep?: number;
  className?: string;
  image?: string;
  unit?: "mm" | "cm";
  inputVersion?: string;
  additionalLabel?: string;
  disabled?: boolean;
  disabledMin?: boolean;
  disabledMax?: boolean;
  mask?: number;
}

export const NumberInput: React.FC<NumberInputProps> = ({
  image,
  value,
  onChange,
  min,
  max,
  step,
  label,
  showSubLabel = true,
  subStep,
  className,
  unit = "mm",
  inputVersion,
  additionalLabel,
  disabled = false,
  disabledMin = false,
  disabledMax = false,
  mask = 0,
  realMin = 0,
}) => {
  const initialDisplay = fromMmToUnit((value ?? 0) - mask, unit).toString();
  const [inputValue, setInputValue] = useState<string>(initialDisplay);
  const [debouncedValue] = useDebounce(inputValue, 300);
  const [initialize, setInitialize] = React.useState(false);
  const [isNotValid, setIsNotValid] = useState<boolean>(false);
  const ref = useRef<HTMLInputElement>(null);

  const displayedMin = fromMmToUnit(min - mask, unit);
  const displayedMax = fromMmToUnit(max - mask, unit);
  const stepForInput = fromMmToUnit(step, unit);
  const subStepForInput = fromMmToUnit(subStep ?? 0, unit);

  const current = parseInt(inputValue, 10);
  const atMin = isNaN(current) || current <= displayedMin;
  const atMax = isNaN(current) || current >= displayedMax;
  const atSubMin =
    isNaN(current) ||
    current - (subStepForInput ?? stepForInput) < displayedMin;
  const atSubMax =
    isNaN(current) ||
    current + (subStepForInput ?? stepForInput) > displayedMax;

  useEffect(() => {
    if (ref && ref.current) {
      ref.current.dispatchEvent(new Event("blur", { bubbles: true }));
      ref.current.value = fromMmToUnit(value - mask, unit).toString();
      setInputValue(fromMmToUnit(value - mask, unit).toString());
    }
  }, [inputVersion]);

  useEffect(() => {
    setIsNotValid(false);

    if (!initialize) {
      setInitialize(true);
    } else {
      const parsedValue = parseInt(debouncedValue, 10);

      const parsedValueMm = isNaN(parsedValue)
        ? NaN
        : fromUnitToMm(parsedValue, unit);
      const realMm = isNaN(parsedValueMm) ? NaN : parsedValueMm + (mask ?? 0);

      const lowerMm = Math.floor(Math.max(min, realMin));
      const upperMm = Math.floor(max);

      if (!isNaN(parsedValue) && !isNaN(realMm)) {
        if (realMm >= lowerMm && realMm <= upperMm) {
          handleChange(parsedValue);
          setInputValue(parsedValue.toString());
          setIsNotValid(false);
        } else {
          const clampedMm = Math.min(Math.max(realMm, lowerMm), upperMm);
          const displayToApply = fromMmToUnit(clampedMm - (mask ?? 0), unit);
          handleChange(displayToApply);
          setInputValue(displayToApply.toString());
          setIsNotValid(true);
        }
      } else {
        const previousDisplay = fromMmToUnit(value - (mask ?? 0), unit);
        setInputValue(previousDisplay.toString());
        setIsNotValid(true);
      }
    }
  }, [debouncedValue]);

  const roundValue = (num: number, numStep: number) => {
    if (displayedMin !== undefined && num < displayedMin) {
      num = displayedMin;
    } else if (displayedMax !== undefined && num > displayedMax) {
      num = displayedMax;
    }

    return Math.round(num / numStep) * numStep;
  };

  const debounceChange = (newVal: number) => {
    setInputValue(newVal.toString());
  };

  const handleChange = (displayValueForApply: number) => {
    if (onChange && !disabled) {
      const mm = fromUnitToMm(displayValueForApply, unit) + (mask ?? 0);
      onChange(mm);
    }
  };

  const handleMinusClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const current = parseInt(inputValue, 10) || 0;
    const roundedValue = roundValue(current - stepForInput, stepForInput);
    debounceChange(roundedValue);
  };

  const handlePlusClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const current = parseInt(inputValue, 10) || 0;
    const roundedValue = roundValue(current + stepForInput, stepForInput);
    debounceChange(roundedValue);
  };

  const handleSubMinusClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const current = parseInt(inputValue, 10) || 0;
    const usedStep = subStepForInput ?? stepForInput;
    const roundedValue = roundValue(current - usedStep, usedStep);
    debounceChange(roundedValue);
  };

  const handleSubPlusClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const current = parseInt(inputValue, 10) || 0;
    const usedStep = subStepForInput ?? stepForInput;
    const roundedValue = roundValue(current + usedStep, usedStep);
    debounceChange(roundedValue);
  };

  const handleInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
  };

  const handleInputValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
  };

  useEffect(() => {
    setInputValue(fromMmToUnit(value - mask, unit).toString());
  }, [unit, value]);

  return (
    <div className={`number-input ${className || ""}`}>
      <div className={`number-input__label number-input${className || ""}`}>
        <div className="start-option-container">
          {image && (
            <img
              src={image}
              alt={label}
              className="number-input__image option-icon-svg"
            />
          )}
          {label && (
            <>
              <div className={"dimension-label"}>{label}</div>
            </>
          )}
        </div>
        {showSubLabel && (
          <span>
            (min. {displayedMin} - max. {displayedMax})
          </span>
        )}
      </div>

      <div className="number-input__wrapper">
        {subStep && (
          <button
            title="Verminderen"
            className="number-input__minus--sub"
            onClick={handleSubMinusClick}
            disabled={atSubMin || disabled || disabledMin}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="icon-plus"
            >
              <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" />
            </svg>
          </button>
        )}
        <button
          title="Verminderen"
          className="number-input__minus"
          onClick={handleMinusClick}
          disabled={atMin || disabled || disabledMin}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="icon-plus"
          >
            <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" />
          </svg>
        </button>
        <div className={`number-input__input ${isNotValid ? "no-valid" : ""}`}>
          <input
            title="Invoer"
            ref={ref}
            type="number"
            value={inputValue}
            onChange={handleInputValue}
            onClick={handleInputClick}
            min={displayedMin}
            max={displayedMax}
            step={stepForInput}
          />
          <span className="number-input__unit">{unit}</span>
        </div>
        <button
          title="Toenemen"
          className="number-input__plus"
          onClick={handlePlusClick}
          disabled={atMax || disabled || disabledMax}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="icon-plus"
          >
            <line x1="6" y1="0" x2="6" y2="12" stroke="currentColor" />
            <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" />
          </svg>
        </button>
        {subStep && (
          <button
            title="Toenemen"
            className="number-input__plus--sub"
            onClick={handleSubPlusClick}
            disabled={atSubMax || disabled || disabledMax}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="icon-plus"
            >
              <line x1="6" y1="0" x2="6" y2="12" stroke="currentColor" />
              <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};
